import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttDataWaitingAlert } from "@/components/generic/mqtt-data-waiting-alert";
import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyCard } from "@/components/generic/multi-property-card";
import { MultiPropertyCardList } from "@/components/generic/multi-property-card-list";

import { getQueuedJobsProperties } from "@/components/milling-jobs/queued-job-list";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ApiError } from "@/data-interfaces/generics";
import type {
	QueuedMillingJob,
	QueuedMillingJobDetails,
} from "@/data-interfaces/milling-jobs";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import {
	getJobBlocks,
	getToolWear,
} from "@/lib/data-remappings/queued-job-details";
import {
	getAppWrappedPageUrl,
	MILLING_JOBS_PAGE_URL,
	QUEUED_JOB_DETAILS_PAGE_URL,
} from "@/lib/page-routes";
import {
	MJ_QUEUED_JOB_DETAILS_URL,
	MJ_QUEUED_JOB_URL,
} from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(QUEUED_JOB_DETAILS_PAGE_URL),
)({
	component: QueuedJobDetails,
	validateSearch: (search: QueuedMillingJob) => search,
});

function QueuedJobDetails() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const queuedJobSearchParam: QueuedMillingJob = Route.useSearch();

	const [queuedJob, setQueuedJob] = useState<QueuedMillingJob | ApiError>(
		queuedJobSearchParam,
	);
	const [jobDetails, setJobDetails] = useState<
		QueuedMillingJobDetails | ApiError | null
	>(null);

	useMQTTSubscribe(MJ_QUEUED_JOB_URL, setQueuedJob);
	useMQTTSubscribe(MJ_QUEUED_JOB_DETAILS_URL, setJobDetails);

	useMQTTAutoRefreshPublish(20, MJ_QUEUED_JOB_URL, {
		job_id: queuedJobSearchParam.id,
	});
	useMQTTAutoRefreshPublish(20, MJ_QUEUED_JOB_DETAILS_URL, {
		job_id: queuedJobSearchParam.id,
	});

	return (
		<div className="text-white p-8 flex flex-col flex-1 gap-y-4">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type="button"
					onClick={() => {
						navigate({
							to: MILLING_JOBS_PAGE_URL,
							search: () => ({ selectedTab: "queue" }),
						});
					}}
				>
					{"< "}
					{t("Jobs")}
				</button>
			</h2>

			<div className="flex flex-col gap-y-4">
				<h2 className="text-2xl sm:text-3xl">{t("Selected job")}</h2>
				<MqttSuspense
					objects={queuedJob}
					requestUrl={MJ_QUEUED_JOB_URL}
					parameters={{ job_id: queuedJobSearchParam.id }}
					genericError="Error retriving job"
				>
					{(queuedJob) => (
						<MultiPropertyCard
							multiPropertyObject={{
								title: queuedJob.name,
								properties: getQueuedJobsProperties(queuedJob),
							}}
						/>
					)}
				</MqttSuspense>
			</div>

			<div className="flex flex-1 grow justify-center">
				<Tabs defaultValue="job-details" className="flex flex-1 gap-y-8 mt-8">
					<div className="flex flex-row justify-center">
						<TabsList>
							<TabsTrigger value="job-details" className="min-w-25">
								{t("Job details")}
							</TabsTrigger>
							<TabsTrigger value="tool-wear" className="min-w-25">
								{t("Tool wear")}
							</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent value="job-details">
						<MqttSuspense
							objects={jobDetails}
							genericError="Error retriving job details"
						>
							{(jobDetails) => (
								<MultiPropertyCardList
									multiPropertyObjects={getJobBlocks(jobDetails.blocks)}
									noItemsLabel="No job blocks for queued job"
									pagination={true}
								/>
							)}
						</MqttSuspense>
					</TabsContent>

					<TabsContent value="tool-wear">
						<MqttSuspense
							objects={jobDetails}
							genericError="Error retriving job details"
						>
							{(jobDetails) => (
								<MultiPropertyCardList
									multiPropertyObjects={getToolWear(jobDetails.tools)}
									noItemsLabel="No tool wear info"
								/>
							)}
						</MqttSuspense>
					</TabsContent>
				</Tabs>
			</div>

			<MqttDataWaitingAlert
				dataToWait={jobDetails}
				requestUrl={MJ_QUEUED_JOB_DETAILS_URL}
				parameters={{ job_id: queuedJobSearchParam.id }}
			/>
		</div>
	);
}
